import React, { useState, useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Typography } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/pro-solid-svg-icons";
import { datadogRum } from "@datadog/browser-rum";
import ReactMarkdown from "react-markdown";
import ReactPlayer from "react-player";
import { useTranslation } from "react-i18next";
import consentMarkdown from "./consent.md?raw";
import handshakeIcon from "./handshake.svg";
import unityBrandLogo from "../../assets/logos/unity-rebrand/unity-horiz-cobranded.svg";
import unityAneuploidyInfoVideo from "../../assets/images/unityAneuploidyInfoVideo.png";
import allAboutCarrierVideo from "../../assets/images/allAboutCarrierVideo.png";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import {
  Accordions,
  LinkedAccordian,
} from "../../components/shared/Accordion/Accordions";
import { GetTypographyContent } from "../../components/shared/Typography/Typography";
import { Banner } from "../../components/Banner/Banner";
import { SpinnerLoading } from "../../components/shared/Spinner/SpinnerLoading";
import { HELP_PAGE_URL } from "../../utils/urlConstants";
import { landingPageAccordions } from "../Landing/LandingHelpData";
import { HeaderIcon } from "../../components/Header/Header.styles";
import {
  LandingPageWrapper,
  LandingGeneralContainer,
  LandingPageAccordionWrapper,
  LandingPageTitleWrapper,
  PublicConsentWrapper,
  PublicConsentHeader,
  PublicConsentIcon,
  ConsentFormContainer,
  ConsentText,
  FormGrid,
  FormField,
  DateField,
  ConsentButton,
  SuccessContainer,
  SuccessIcon,
  SuccessText,
  ErrorText,
  LoadingSpinner,
  DesktopContentGrid,
  FormSectionWrapper,
  VideoSectionWrapper,
  VideoPlayersContainer,
  ConsentVideoWrapper,
  ConsentMarkdownContainer,
  ConsentCheckboxContainer,
  ConsentCheckboxContent,
  ConsentCheckbox,
  ConsentCheckboxLabel,
  SignatureNoteContainer,
  ConsentAcknowledgementContainer,
  PublicConsentPageHeader,
  PublicConsentPageContainer,
} from "./PublicConsent.styles";
import {
  ConsentFormData,
  FormValidationErrors,
  ConsentFormState,
} from "./PublicConsent.types";
import { submitPublicConsent } from "./Services/PublicConsentService";
import { TrackAnalyticsEvent } from "../../services/Analytics";
import { AnalyticsEventNames } from "../../utils/analyticsEvents";
import { themePalette } from "../../theme";

// Validation helper functions
const validateAge = (dateOfBirth: string): boolean => {
  if (!dateOfBirth) return false;
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  const age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    return age - 1 >= 18;
  }
  return age >= 18;
};

const validateForm = (
  formData: Omit<ConsentFormData, "portalAccountPublicId">,
  messages: {
    ageRequirement: string;
    confirmConsent: string;
    dateOfBirthRequired: string;
    firstNameRequired: string;
    lastNameRequired: string;
  },
): FormValidationErrors => {
  const errors: FormValidationErrors = {};

  if (!formData.firstName.trim()) {
    errors.firstName = messages.firstNameRequired;
  }

  if (!formData.lastName.trim()) {
    errors.lastName = messages.lastNameRequired;
  }

  if (!formData.dateOfBirth) {
    errors.dateOfBirth = messages.dateOfBirthRequired;
  } else if (!validateAge(formData.dateOfBirth)) {
    errors.dateOfBirth = messages.ageRequirement;
  }

  if (!formData.niptScreenConsentOptIn) {
    errors.testSelection = messages.confirmConsent;
  }

  return errors;
};

const PublicConsent = (): React.ReactElement => {
  const { t } = useTranslation();
  const { deviceType } = useDeviceHook();
  const { portalAccountPublicId } = useParams<{
    portalAccountPublicId: string;
  }>();

  // Form state
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    niptScreenConsentOptIn: false,
  });

  // Submission state
  const [consentState, setConsentState] = useState<ConsentFormState>({
    isLoading: false,
    isSubmitted: false,
    hasError: false,
    errorMessage: "",
  });

  // Validation errors
  const [validationErrors, setValidationErrors] =
    useState<FormValidationErrors>({});

  type DatadogRumWithInternal = typeof datadogRum & {
    getInternalContext?: () => { session_id?: string };
  };

  const getDatadogSessionId = (): string | undefined => {
    const ctx = (datadogRum as DatadogRumWithInternal).getInternalContext?.();
    return ctx?.session_id;
  };

  useEffect(() => {
    const datadogSessionId = getDatadogSessionId();
    if (datadogSessionId) {
      TrackAnalyticsEvent(
        AnalyticsEventNames.ConsentPageViewed,
        undefined,
        datadogSessionId,
        { portal_account_public_id: portalAccountPublicId },
      );
    }
  }, [portalAccountPublicId]);

  useEffect(() => {
    if (!consentState.isSubmitted) {
      return;
    }
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
  }, [consentState.isSubmitted]);

  type TextFieldKey = keyof Pick<
    Omit<ConsentFormData, "portalAccountPublicId" | "niptScreenConsentOptIn">,
    "firstName" | "lastName" | "dateOfBirth"
  >;

  const handleTextFieldChange =
    (field: TextFieldKey) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setFormData((prev) => ({ ...prev, [field]: value }));
      setValidationErrors((prev) =>
        prev[field] ? { ...prev, [field]: undefined } : prev,
      );
    };

  const handleNiptScreenConsentOptInChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = event.target;
      setFormData((prev) => ({
        ...prev,
        niptScreenConsentOptIn: checked,
      }));
      if (validationErrors.testSelection) {
        setValidationErrors((prev) => ({ ...prev, testSelection: undefined }));
      }
      const datadogSessionId = getDatadogSessionId();
      if (datadogSessionId) {
        TrackAnalyticsEvent(
          AnalyticsEventNames.ConsentNiptOptInCheckboxClicked,
          undefined,
          datadogSessionId,
          { portal_account_public_id: portalAccountPublicId },
        );
      }
    },
    [validationErrors.testSelection, portalAccountPublicId],
  );

  const handleSubmit = async () => {
    // Validate form
    const errors = validateForm(formData, {
      ageRequirement: t("publicConsent.errors.ageRequirement"),
      confirmConsent: t("publicConsent.errors.confirmConsent"),
      dateOfBirthRequired: t("publicConsent.errors.dateOfBirthRequired"),
      firstNameRequired: t("publicConsent.errors.firstNameRequired"),
      lastNameRequired: t("publicConsent.errors.lastNameRequired"),
    });
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    // Clear any previous errors
    setValidationErrors({});
    setConsentState((prev) => ({
      ...prev,
      isLoading: true,
      hasError: false,
      errorMessage: "",
    }));

    const datadogSessionId = getDatadogSessionId();

    try {
      const responseStatusCode = await submitPublicConsent({
        public_id: portalAccountPublicId,
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        date_of_birth: formData.dateOfBirth,
        is_nipt_screen_consent: formData.niptScreenConsentOptIn,
      });
      if (responseStatusCode === 201) {
        if (datadogSessionId) {
          TrackAnalyticsEvent(
            AnalyticsEventNames.ConsentSubmitSucceeded,
            undefined,
            datadogSessionId,
            { portal_account_public_id: portalAccountPublicId },
          );
        }
        setConsentState({
          isLoading: false,
          isSubmitted: true,
          hasError: false,
          errorMessage: "",
        });
      } else {
        if (datadogSessionId) {
          TrackAnalyticsEvent(
            AnalyticsEventNames.ConsentSubmitFailed,
            undefined,
            datadogSessionId,
            { portal_account_public_id: portalAccountPublicId },
          );
        }
        setConsentState({
          isLoading: false,
          isSubmitted: false,
          hasError: true,
          errorMessage: t("publicConsent.errors.unexpected"),
        });
      }
    } catch (error) {
      if (datadogSessionId) {
        TrackAnalyticsEvent(
          AnalyticsEventNames.ConsentSubmitFailed,
          undefined,
          datadogSessionId,
          { portal_account_public_id: portalAccountPublicId },
        );
      }
      setConsentState({
        isLoading: false,
        isSubmitted: false,
        hasError: true,
        errorMessage: t("publicConsent.errors.unexpected"),
      });
    }
  };

  // Header component similar to PhlebotomyPage
  const PageHeader = () => (
    <PublicConsentPageHeader>
      <HeaderIcon
        src={unityBrandLogo}
        width="279px"
        alt="Unity by BillionToOne"
      />
    </PublicConsentPageHeader>
  );

  const SuccessMessage = () => (
    <ConsentFormContainer>
      <SuccessContainer>
        <SuccessIcon>
          <FontAwesomeIcon icon={faCircleCheck} />
        </SuccessIcon>
        <SuccessText>
          {GetTypographyContent({
            content: t("publicConsent.successMessage"),
            size: "medium",
          })}
        </SuccessText>
      </SuccessContainer>
    </ConsentFormContainer>
  );

  return (
    <PublicConsentPageContainer>
      <PageHeader />
      <LandingPageWrapper>
        <PublicConsentWrapper deviceType={deviceType}>
          {/* Header Section */}
          <PublicConsentHeader>
            {GetTypographyContent({
              content: t("publicConsent.welcome"),
              size: "h1",
              textColor: "greyMedium",
              isBold: true,
            })}
          </PublicConsentHeader>

          {/* Error Banner */}
          {consentState.hasError && (
            <Banner
              bannerType="warning"
              bannerTitle={t("publicConsent.submissionError")}
              bannerText={consentState.errorMessage}
              bannerActionCTA={t("publicConsent.tryAgain")}
              bannerActionOnClick={() =>
                setConsentState((prev) => ({
                  ...prev,
                  hasError: false,
                  errorMessage: "",
                }))
              }
            />
          )}

          <DesktopContentGrid deviceType={deviceType}>
            {/* Form Section */}
            <FormSectionWrapper>
              {/* Consent Form or Success Message */}
              {consentState.isSubmitted ? (
                <SuccessMessage />
              ) : (
                <ConsentFormContainer>
                  <PublicConsentHeader>
                    <PublicConsentIcon>
                      <img
                        src={handshakeIcon}
                        alt={t("publicConsent.title")}
                        width="45"
                        height="28"
                      />
                    </PublicConsentIcon>
                    {GetTypographyContent({
                      content: t("publicConsent.title"),
                      size: "h2",
                      textColor: "greyMedium",
                      isBold: true,
                    })}
                  </PublicConsentHeader>

                  {/* Video Section - Inside Patient Consent Box */}
                  <ConsentText>
                    {GetTypographyContent({
                      content: t("publicConsent.videoDescription"),
                      size: "medium",
                    })}
                  </ConsentText>
                  <VideoPlayersContainer>
                    {/* Aneuploidy Informational video */}
                    <ConsentVideoWrapper
                      deviceType={deviceType}
                      hasBottomMargin
                    >
                      <ReactPlayer
                        playing
                        url="https://player.vimeo.com/video/960668298"
                        light={unityAneuploidyInfoVideo}
                        height={deviceType !== "mobile" ? "100%" : "328px"}
                        width="100%"
                        controls
                        onPlay={async () => {
                          const datadogSessionId = getDatadogSessionId();
                          if (datadogSessionId) {
                            TrackAnalyticsEvent(
                              AnalyticsEventNames.ConsentVideoPlayClicked,
                              undefined,
                              datadogSessionId,
                              {
                                portal_account_public_id: portalAccountPublicId,
                              },
                            );
                          }
                        }}
                      />
                    </ConsentVideoWrapper>

                    {/* Unity Informational video */}
                    <ConsentVideoWrapper deviceType={deviceType}>
                      <ReactPlayer
                        playing
                        url="https://player.vimeo.com/video/960668319"
                        light={allAboutCarrierVideo}
                        height={deviceType !== "mobile" ? "100%" : "328px"}
                        width="100%"
                        controls
                        onPlay={async () => {
                          const datadogSessionId = getDatadogSessionId();
                          if (datadogSessionId) {
                            TrackAnalyticsEvent(
                              AnalyticsEventNames.ConsentVideoPlayClicked,
                              undefined,
                              datadogSessionId,
                              {
                                portal_account_public_id: portalAccountPublicId,
                              },
                            );
                          }
                        }}
                      />
                    </ConsentVideoWrapper>
                  </VideoPlayersContainer>

                  <ConsentText>
                    {GetTypographyContent({
                      content: (
                        <a
                          href="https://www.acog.org/womens-health/videos/prenatal-genetic-testing"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: themePalette.greenDark }}
                        >
                          {t("publicConsent.learnMoreLink")}
                        </a>
                      ),
                      size: "medium",
                    })}
                  </ConsentText>

                  <ConsentMarkdownContainer>
                    <ReactMarkdown>{consentMarkdown}</ReactMarkdown>
                  </ConsentMarkdownContainer>

                  <ConsentCheckboxContainer>
                    <ConsentCheckboxContent>
                      <ConsentCheckboxLabel
                        control={
                          <ConsentCheckbox
                            checked={formData.niptScreenConsentOptIn}
                            onChange={handleNiptScreenConsentOptInChange}
                          />
                        }
                        label={t("publicConsent.consentCheckbox")}
                      />
                    </ConsentCheckboxContent>
                    {validationErrors.testSelection && (
                      <ErrorText>{validationErrors.testSelection}</ErrorText>
                    )}
                  </ConsentCheckboxContainer>

                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleSubmit();
                    }}
                  >
                    <FormGrid deviceType={deviceType}>
                      <div>
                        <FormField
                          fullWidth
                          label={t("publicConsent.firstName")}
                          value={formData.firstName}
                          onChange={handleTextFieldChange("firstName")}
                          error={!!validationErrors.firstName}
                          placeholder={t("publicConsent.firstNamePlaceholder")}
                          variant="outlined"
                        />
                        {validationErrors.firstName && (
                          <ErrorText>{validationErrors.firstName}</ErrorText>
                        )}
                      </div>

                      <div>
                        <FormField
                          fullWidth
                          label={t("publicConsent.lastName")}
                          value={formData.lastName}
                          onChange={handleTextFieldChange("lastName")}
                          error={!!validationErrors.lastName}
                          placeholder={t("publicConsent.lastNamePlaceholder")}
                          variant="outlined"
                        />
                        {validationErrors.lastName && (
                          <ErrorText>{validationErrors.lastName}</ErrorText>
                        )}
                      </div>

                      <div>
                        <DateField
                          fullWidth
                          type="date"
                          label={t("publicConsent.dateOfBirth")}
                          value={formData.dateOfBirth}
                          onChange={handleTextFieldChange("dateOfBirth")}
                          error={!!validationErrors.dateOfBirth}
                          InputLabelProps={{
                            shrink: true,
                          }}
                          variant="outlined"
                        />
                        {validationErrors.dateOfBirth && (
                          <ErrorText>{validationErrors.dateOfBirth}</ErrorText>
                        )}
                      </div>

                      {/* Electronic signature note under name field */}
                      <SignatureNoteContainer deviceType={deviceType}>
                        <Typography
                          variant="body2"
                          sx={{ color: themePalette.greyMedium }}
                        >
                          {t("publicConsent.electronicSignature")}
                        </Typography>
                      </SignatureNoteContainer>
                    </FormGrid>

                    {/* Consent to Electronic Signature statement above submit */}
                    <ConsentAcknowledgementContainer>
                      <Typography
                        variant="body2"
                        sx={{ color: themePalette.greyMedium }}
                      >
                        {t("publicConsent.signatureAcknowledgement")}
                      </Typography>
                    </ConsentAcknowledgementContainer>

                    {consentState.isLoading ? (
                      <LoadingSpinner>
                        <SpinnerLoading />
                        <span className="loading-text">
                          {t("publicConsent.submittingConsent")}
                        </span>
                      </LoadingSpinner>
                    ) : (
                      <ConsentButton
                        type="submit"
                        disabled={consentState.isLoading}
                      >
                        {t("publicConsent.iAgree")}
                      </ConsentButton>
                    )}
                  </form>
                </ConsentFormContainer>
              )}
            </FormSectionWrapper>

            {/* FAQ Section */}
            <VideoSectionWrapper deviceType={deviceType}>
              {/* FAQ Section - Same as Landing */}
              <div>
                <LandingPageTitleWrapper>
                  {GetTypographyContent({
                    content: t("publicConsent.haveAQuestion"),
                    size: deviceType === "mobile" ? "xlarge" : "large",
                    textColor: "greyMedium",
                    isBold: true,
                  })}
                </LandingPageTitleWrapper>
                <LandingGeneralContainer padding="0px">
                  <LandingPageAccordionWrapper>
                    <Accordions
                      data={landingPageAccordions("", t)} // Empty requisition ID for public view
                      isLinked={deviceType !== "mobile"}
                      size="small"
                    />
                    <LinkedAccordian
                      id="help"
                      key="help"
                      panelKey="help"
                      title={t("publicConsent.visitFaqs")}
                      onClick={() => {
                        window.open(HELP_PAGE_URL, "_blank");
                      }}
                      size="large"
                    />
                  </LandingPageAccordionWrapper>
                </LandingGeneralContainer>
              </div>
            </VideoSectionWrapper>
          </DesktopContentGrid>
        </PublicConsentWrapper>
      </LandingPageWrapper>
    </PublicConsentPageContainer>
  );
};

export default PublicConsent;
